// InsightsStrip — compact "what changed this month" strip of the biggest
// movers pulled from AA Insights. This is the homepage headline signal: a
// single glance at what moved, in which direction, and by how much.
//
// Self-contained: no imports beyond React. Core palette + named dash-*
// type-scale tokens only — no raw hex, no hardcoded Tailwind palette classes.

export type MoverDirection = "up" | "down";

export interface Mover {
  /** What changed, e.g. "Promotion views". */
  label: string;
  /**
   * Magnitude of the change, as a percent (e.g. 21 for a 21% move). The
   * sign shown to the user is derived from `direction`, not from the sign
   * of this number — pass either a signed or unsigned value, both render
   * correctly.
   */
  change_pct: number;
  /** Whether the metric moved up or down this period. */
  direction: MoverDirection;
  /** Optional data source tag, e.g. "GA4", "WhatConverts", "Google Ads". */
  provider?: string;
}

export interface InsightsStripProps {
  /** Biggest movers this period, from AA Insights. Empty renders a fallback. */
  movers: Mover[];
}

// Sample data for standalone preview / storybook-style rendering.
export const demoInsights: Mover[] = [
  { label: "Promotion views", change_pct: 100, direction: "up", provider: "GA4" },
  { label: "Leads", change_pct: 21, direction: "up", provider: "WhatConverts" },
  { label: "Engagement/user", change_pct: 56, direction: "down", provider: "GA4" },
  { label: "Ad CPC", change_pct: 14, direction: "up", provider: "Google Ads" },
];

// Small solid triangle, flipped for "down". Uses currentColor so it always
// matches the up/down tone applied to its wrapping element.
function ChangeArrow({ direction }: { direction: MoverDirection }) {
  const up = direction === "up";
  return (
    <svg
      width="9"
      height="9"
      viewBox="0 0 10 10"
      aria-hidden="true"
      className="shrink-0"
      style={{ transform: up ? undefined : "rotate(180deg)" }}
    >
      <path d="M5 0L10 9H0L5 0Z" fill="currentColor" />
    </svg>
  );
}

// Absurd-percentage floor (task #24) -- same rule, same 999 (>=10x)
// threshold, as `IMPLAUSIBLE_PCT_MAGNITUDE` in
// components/client-analytics/primitives.tsx (see the long comment above
// TrendBadge there for the full rationale and the four confirmed live
// examples: +32,773%/+37,321%/+5,238%/+1080%). Duplicated as a bare
// constant rather than imported because this file's own header commits it
// to zero imports beyond React (self-contained, drop-in-able on its own) --
// the RULE lives in one place conceptually (same number, same reasoning,
// cross-referenced here), even though the literal value has to be copied
// across the import boundary. `Mover.change_pct` (like `InsightMover` it's
// mapped from -- see types/clientAnalytics.ts) never carries the
// underlying prior/current counts, only the already-divided percentage, so
// this is the same magnitude-only proxy ChangePctBadge uses, not a
// baseline-value check.
const IMPLAUSIBLE_PCT_MAGNITUDE = 999;

function InsightChip({ mover }: { mover: Mover }) {
  const { label, change_pct, direction, provider } = mover;
  const up = direction === "up";
  const implausible = Number.isFinite(change_pct) && Math.abs(change_pct) >= IMPLAUSIBLE_PCT_MAGNITUDE;
  // Direction color lives ONLY on the arrow + percent — the chip itself
  // stays a neutral card so a row of movers doesn't read as a wall of
  // red/green. A floored (implausible-magnitude) mover drops the color
  // entirely -- the specific number isn't trustworthy, so neither is
  // "good news" framing built on top of it.
  const tone = implausible
    ? "var(--color-muted-foreground)"
    : up
      ? "hsl(var(--status-success))"
      : "hsl(var(--status-error))";
  const magnitude = Math.round(Math.abs(change_pct));

  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5">
      <span className="text-dash-context font-medium text-foreground whitespace-nowrap">
        {label}
      </span>
      <span
        className="inline-flex items-center gap-1 text-dash-subtitle font-bold tabular whitespace-nowrap"
        style={{ color: tone }}
      >
        <ChangeArrow direction={direction} />
        {implausible ? "thin baseline" : `${up ? "+" : "-"}${magnitude}%`}
      </span>
      {provider && (
        <span className="text-dash-context font-medium text-muted-foreground whitespace-nowrap">
          {provider}
        </span>
      )}
    </div>
  );
}

export function InsightsStrip({ movers }: InsightsStripProps) {
  if (!movers || movers.length === 0) {
    return (
      <div className="text-dash-subtitle text-muted-foreground">
        No notable changes this period
      </div>
    );
  }

  return (
    <div
      className="flex flex-wrap items-center gap-2.5"
      role="list"
      aria-label="Biggest changes this period"
    >
      {movers.map((mover, i) => (
        <div role="listitem" key={`${mover.label}-${i}`}>
          <InsightChip mover={mover} />
        </div>
      ))}
    </div>
  );
}

export default InsightsStrip;
